import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Table } from 'antd'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'

import { showErrorNotification } from '../../../core/helpers'
import { useQueryParams } from '../../../core/hooks'

import { PAGINATION } from '../../../common/constants'

import { useLoggedInUser } from '../../../account/hooks'
import { usePriorityTasksMenuCurationTasksQuery } from '../../hooks'
import { createPriorityTaskMenuCurationTasksColumns } from '../../services'

import PriorityTaskBulkRemoveAllocationsButton from './priority-task-bulk-remove-allocations-button'

const PriorityTaskMenuCurationTaskTable = ({ priorityTask }) => {
  const { settings: userSettings } = useLoggedInUser()
  const [selectedAllocationIds, setSelectedAllocationIds] = useState([])

  const {
    allocatedUserId = null,
    allocationStatus = null,
    currentPage = PAGINATION.DEFAULT_PAGE,
    pageSize = userSettings?.pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
    setQueryParams,
  } = useQueryParams()

  const { filters, pagination } = useMemo(
    () => ({
      filters: {
        priorityTaskId: priorityTask.id,
        allocatedUserId,
        allocationStatus,
      },
      pagination: {
        pageSize,
        currentPage,
      },
    }),
    [priorityTask.id, allocatedUserId, allocationStatus, pageSize, currentPage],
  )

  const { data, isLoading, error } = usePriorityTasksMenuCurationTasksQuery(
    filters,
    pagination,
  )
  const { result, total } = data || {}

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Failed to get menu curation task',
        description: error,
      })
    }
  }, [error])

  const handleChangePagination = useCallback(
    (page, size) =>
      setQueryParams({
        currentPage: size !== pageSize ? PAGINATION.DEFAULT_PAGE : page,
        pageSize: size,
      }),
    [pageSize, setQueryParams],
  )

  const tableColumns = useMemo(
    () =>
      createPriorityTaskMenuCurationTasksColumns(
        priorityTask.id,
        selectedAllocationIds,
        setSelectedAllocationIds,
        result,
      ),
    [priorityTask.id, selectedAllocationIds, result],
  )

  const paginationConfig = useMemo(() => {
    if (isNil(total)) {
      return false
    }

    return {
      current: Number(currentPage),
      pageSize,
      total,
      onChange: handleChangePagination,
      onShowSizeChange: handleChangePagination,
      pageSizeOptions: PAGINATION.DEFAULT_PAGINATION_SIZE_OPTIONS,
    }
  }, [total, currentPage, pageSize, handleChangePagination])

  return (
    <>
      <PriorityTaskBulkRemoveAllocationsButton
        selectedAllocationIds={selectedAllocationIds}
        onSelectionChange={setSelectedAllocationIds}
      />
      <Table
        loading={isLoading}
        rowKey="id"
        columns={tableColumns}
        dataSource={result}
        pagination={paginationConfig}
        scroll={{ x: 'max-content' }}
      />
    </>
  )
}

PriorityTaskMenuCurationTaskTable.propTypes = {
  priorityTask: PropTypes.object.isRequired,
}

export default PriorityTaskMenuCurationTaskTable
